import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SafetyShell from "../safety/SafetyShell";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BarChart3, Download, RefreshCw, Ship, CircleDot, DollarSign, IndianRupee, Truck, FileText, ExternalLink, Clock, CheckCircle2, AlertTriangle, RotateCcw, ShieldCheck, Upload, X, Filter, XCircle, FileSpreadsheet, ArrowUp, ArrowDown, ChevronsUpDown, Container } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, LabelList, Legend } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const fmtUSD = (n) =>
  n == null ? "—" : n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const fmtINR = (n) =>
  n == null ? "—" : n.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? d : "—");
const fmtCompact = (n, cur) => {
  if (n == null) return "";
  const abs = Math.abs(n);
  if (cur === "usd") {
    if (abs >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
    if (abs >= 1000) return `$${(n / 1000).toFixed(0)}k`;
    return `$${Math.round(n)}`;
  }
  // INR — Indian numbering (crore / lakh)
  if (abs >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (abs >= 1000) return `₹${(n / 1000).toFixed(0)}k`;
  return `₹${Math.round(n)}`;
};

function ManagementReport({ user, onLogout }) {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [data, setData] = useState(null);
  const [domestic, setDomestic] = useState(null); // domestic (INR) invoices for drill-in
  const [domView, setDomView] = useState({ open: false, inv: null, data: null, loading: false });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | dispatched | not_dispatched
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [groupBy, setGroupBy] = useState("month"); // none | month | week
  const [dateBasis, setDateBasis] = useState("dispatch"); // po | dispatch (chart/grouping basis)
  const [preview, setPreview] = useState({ open: false, sbId: null, plId: null, title: "", view: "sb" });
  const [recon, setRecon] = useState({}); // period -> reconciliation
  const [canReconcile, setCanReconcile] = useState(false);
  const [showUnreconciledOnly, setShowUnreconciledOnly] = useState(false);
  const [reconDlg, setReconDlg] = useState({ open: false, group: null, cutoff: "", note: "", busy: false });
  const [monthFilter, setMonthFilter] = useState(null); // period 'YYYY-MM' | null — filters PO list below
  const [verticalTab, setVerticalTab] = useState("all"); // 'all' | vertical_id | '__unassigned__'
  const [awaitingOpen, setAwaitingOpen] = useState(false);
  const [sort, setSort] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mgmt_po_sort")) || { key: null, dir: "asc" }; }
    catch { return { key: null, dir: "asc" }; }
  }); // PO table column sort (persisted)
  const [groupSort, setGroupSort] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mgmt_group_sort")) || { key: null, dir: "asc" }; }
    catch { return { key: null, dir: "asc" }; }
  }); // monthly/weekly summary table sort (persisted)
  const [tally, setTally] = useState(null); // { has_import, import, results, months, summary }
  const [tallyBusy, setTallyBusy] = useState(false);
  const [tallyPanel, setTallyPanel] = useState({ open: false, period: null }); // errors panel
  const [resolveDlg, setResolveDlg] = useState({ open: false, row: null, note: "", busy: false });

  const resolveTallyIssue = async () => {
    if (!resolveDlg.note.trim()) { toast.error("Please add a note"); return; }
    setResolveDlg((d) => ({ ...d, busy: true }));
    try {
      await axios.post(`${API}/management/tally/resolve`, { invoice_no: resolveDlg.row.invoice_no, note: resolveDlg.note.trim() }, authHeaders());
      toast.success("Discrepancy resolved");
      setResolveDlg({ open: false, row: null, note: "", busy: false });
      await loadTally();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resolve");
      setResolveDlg((d) => ({ ...d, busy: false }));
    }
  };

  const reopenTallyIssue = async (invoiceNo) => {
    try {
      await axios.delete(`${API}/management/tally/resolve/${encodeURIComponent(invoiceNo)}`, authHeaders());
      toast.success("Re-opened");
      await loadTally();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reopen"); }
  };
  const tallyFileRef = useRef(null);

  const canView = permissions.includes("management_report.view");
  const canViewPL = permissions.includes("packing_list.view");

  const isMonthKey = (p) => /^\d{4}-\d{2}$/.test(p || "");
  const monthEnd = (p) => {
    // last calendar day of the month, formatted from LOCAL parts (no toISOString —
    // that converts local midnight to UTC and rolls back a day in IST/+ zones).
    const y = +p.slice(0, 4), m = +p.slice(5, 7);
    const d = new Date(y, m, 0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const fetchRecon = async () => {
    try {
      const r = await axios.get(`${API}/management/reconciliations`, authHeaders());
      setRecon(r.data.reconciliations || {});
      setCanReconcile(!!r.data.can_reconcile);
    } catch (_) { /* ignore */ }
  };

  const openReconcile = (g) => {
    const isCurrent = g.period === todayISO().slice(0, 7);
    const cutoff = isCurrent ? todayISO() : monthEnd(g.period);
    setReconDlg({ open: true, group: g, cutoff, note: "", busy: false });
  };

  const submitReconcile = async () => {
    const { group, cutoff, note } = reconDlg;
    if (!cutoff) { toast.error("Pick a cutoff date"); return; }
    setReconDlg((d) => ({ ...d, busy: true }));
    try {
      const r = await axios.post(`${API}/management/reconcile`, { period: group.period, cutoff_date: cutoff, note }, authHeaders());
      toast.success(`${group.label} reconciled (${r.data.status}) through ${cutoff}`);
      setReconDlg({ open: false, group: null, cutoff: "", note: "", busy: false });
      fetchRecon();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reconcile failed");
      setReconDlg((d) => ({ ...d, busy: false }));
    }
  };

  const reopenMonth = async (g) => {
    const reason = window.prompt(`Reopen ${g.label}? This clears its reconciled status. Reason:`);
    if (reason == null) return;
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    try {
      await axios.post(`${API}/management/reconcile/reopen`, { period: g.period, reason: reason.trim() }, authHeaders());
      toast.success(`${g.label} reopened`);
      fetchRecon();
    } catch (e) { toast.error(e.response?.data?.detail || "Reopen failed"); }
  };

  const loadTally = async () => {
    try {
      const r = await axios.get(`${API}/management/tally/reconciliation`, authHeaders());
      setTally(r.data?.has_import ? r.data : { has_import: false });
    } catch (_) { setTally({ has_import: false }); }
  };

  const onTallyFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    setTallyBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/management/tally/upload`, fd, {
        headers: { ...authHeaders().headers, "Content-Type": "multipart/form-data" },
      });
      setTally({ has_import: true, ...r.data });
      const s = r.data.summary;
      toast.success(`Tally imported: ${s.matched} matched, ${s.mismatched} mismatched, ${s.missing_in_erp + s.missing_in_tally} unmatched`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Tally upload failed");
    } finally {
      setTallyBusy(false);
    }
  };

  const clearTally = async () => {
    if (!window.confirm("Remove the uploaded Tally file and its reconciliation?")) return;
    try {
      await axios.delete(`${API}/management/tally/import`, authHeaders());
      setTally({ has_import: false });
      toast.success("Tally data cleared");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to clear"); }
  };

  const openSbPreview = (sbId) => {
    if (!sbId) return;
    window.open(`${API}/shipping-bills/${sbId}/preview?token=${localStorage.getItem("token")}`, "_blank", "noopener,noreferrer,width=1000,height=800");
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me/permissions`, authHeaders());
        setPermissions(r.data.effective_permissions || []);
      } catch (_) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (permissions.length === 0) return;
    if (!canView) { setLoading(false); return; }
    fetchReport();
    fetchDomestic();
    if (groupBy === "month") fetchRecon();
    loadTally();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, dateFrom, dateTo, groupBy, dateBasis, permissions.length]);

  const fetchDomestic = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const qs = params.toString();
      const r = await axios.get(`${API}/management/domestic-report${qs ? `?${qs}` : ""}`, authHeaders());
      setDomestic(r.data);
    } catch (_) { /* domestic drill-in is optional */ }
  };

  const openDomView = async (inv) => {
    setDomView({ open: true, inv, data: null, loading: true });
    try {
      const r = await axios.get(`${API}/packing-lists/${inv.id}/domestic-lines`, authHeaders());
      setDomView({ open: true, inv, data: r.data, loading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invoice");
      setDomView({ open: false, inv: null, data: null, loading: false });
    }
  };

  const downloadDomInvoice = async (inv) => {
    if (!inv?.id) return;
    try {
      const res = await axios.get(`${API}/packing-lists/${inv.id}/download`, authHeaders());
      const url = res.data?.url;
      if (!url) { toast.error("Original file not available"); return; }
      const a = document.createElement("a");
      a.href = url; a.target = "_blank"; a.rel = "noopener";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ dispatch_status: filter });
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (groupBy !== "none") params.set("group_by", groupBy);
      if (groupBy !== "none") params.set("date_basis", dateBasis);
      const r = await axios.get(`${API}/management/po-report?${params.toString()}`, authHeaders());
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    let headers, rows, name;
    if (groupBy !== "none" && data?.groups?.length) {
      const withRecon = groupBy === "month";
      headers = [groupBy === "month" ? "Month" : "Week", "POs", "Dispatched", "Pending", "Order Value $", "Order Value Rs.", "Avg Lead (days)"];
      if (withRecon) headers = headers.concat(["Reconciled", "Reconciled By", "Reconciled On", "Cutoff"]);
      rows = data.groups.map((g) => {
        const base = [`"${g.label}"`, g.total_count, g.dispatched_count, g.not_dispatched_count, g.total_sales_usd, g.total_sales_inr, g.avg_lead_time_days ?? ""];
        if (withRecon) {
          const rc = recon[g.period];
          const active = rc && (rc.status === "interim" || rc.status === "full");
          base.push(active ? (rc.status === "full" ? "Full" : "Interim") : "No", active ? `"${rc.reconciled_by_name || rc.reconciled_by || ""}"` : "", active ? (rc.reconciled_at || "").slice(0, 10) : "", active ? (rc.cutoff_date || "") : "");
        }
        return base;
      });
      name = `management-summary-${groupBy}-${new Date().toISOString().slice(0, 10)}.csv`;
    } else {
      if (!data?.rows?.length) return;
      headers = ["Sl.No.", "P.O.No.", "P.O.Date", "Sales Price $", "Sales Price Rs.", "Price Source", "FX Rate", "Required Date", "Actual Dispatch Date", "Invoice No.", "Shipping Bill No."];
      rows = data.rows.map((r) => {
        const src = r.price_source === "shipping_bill" ? (r.price_estimated ? (r.sb_count > 1 ? `Shipping Bill est. FOB÷rate (${r.sb_count} bills)` : "Shipping Bill (est. FOB÷rate)") : (r.sb_count > 1 ? `Shipping Bill (${r.sb_count} bills)` : "Shipping Bill")) : "PO prices";
        const invs = (r.shipping_bills || []).map((s) => s.invoice_no).filter(Boolean).join(" | ");
        const sbs = (r.shipping_bills || []).map((s) => s.sb_no).filter(Boolean).join(" | ");
        return [r.sl_no, `"${r.po_number}"`, r.po_date, r.sales_price_usd ?? "", r.sales_price_inr ?? "", `"${src}"`, r.exchange_rate ?? "", r.required_date, r.actual_dispatch_date, `"${invs || r.invoice_no || ""}"`, `"${sbs || r.shipping_bill_no || ""}"`];
      });
      name = `management-po-report-${new Date().toISOString().slice(0, 10)}.csv`;
    }
    const lines = [headers.join(","), ...rows.map((r) => r.join(","))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const monthField = dateBasis === "dispatch" ? "dispatch_month" : "po_month";
  const filteredRows = (data?.rows || []).filter((r) =>
    (!monthFilter || r[monthField] === monthFilter) &&
    (verticalTab === "all" || (r.vertical_id || "__unassigned__") === verticalTab));

  // Column sort — numeric, date and string aware. Nulls always sort last.
  const SORT_TYPES = {
    sl_no: "num", po_number: "str", po_date: "date", sales_price_usd: "num",
    sales_price_inr: "num", exchange_rate: "num", required_date: "date",
    actual_dispatch_date: "date", invoice_sb: "str",
  };
  const sortValue = (r, key) => {
    if (key === "invoice_sb") return (r.shipping_bills || []).map((s) => s.invoice_no).filter(Boolean).join(" | ");
    return r[key];
  };
  const visibleRows = useMemo(() => {
    if (!sort.key) return filteredRows;
    const type = SORT_TYPES[sort.key] || "str";
    const dir = sort.dir === "asc" ? 1 : -1;
    const norm = (v) => {
      if (v == null || v === "") return null;
      if (type === "num") { const n = Number(v); return isNaN(n) ? null : n; }
      if (type === "date") { const t = new Date(v).getTime(); return isNaN(t) ? null : t; }
      return String(v).toLowerCase();
    };
    return [...filteredRows].sort((a, b) => {
      const av = norm(sortValue(a, sort.key));
      const bv = norm(sortValue(b, sort.key));
      if (av == null && bv == null) return 0;
      if (av == null) return 1;   // nulls last regardless of dir
      if (bv == null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filteredRows, sort]);

  const toggleSort = (key) => setSort((s) =>
    s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: "asc" });
  const SortHeader = ({ column, label, align = "left" }) => (
    <th className={`px-3 py-2 font-medium text-${align}`}>
      <button
        type="button"
        onClick={() => toggleSort(column)}
        className={`inline-flex items-center gap-1 hover:text-indigo-600 transition-colors ${sort.key === column ? "text-indigo-700" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}
        data-testid={`mgmt-sort-${column}`}
        title={`Sort by ${label}`}
      >
        {label}
        {sort.key === column
          ? (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
          : <ChevronsUpDown className="w-3 h-3 opacity-30" />}
      </button>
    </th>
  );

  // Persist both sorts so the preferred view survives reloads/visits.
  useEffect(() => { localStorage.setItem("mgmt_po_sort", JSON.stringify(sort)); }, [sort]);
  useEffect(() => { localStorage.setItem("mgmt_group_sort", JSON.stringify(groupSort)); }, [groupSort]);

  // Monthly/weekly summary table sort (numeric fields + period label default).
  const GROUP_SORT_TYPES = {
    period: "period", total_count: "num", dispatched_count: "num", not_dispatched_count: "num",
    total_sales_usd: "num", total_sales_inr: "num", avg_lead_time_days: "num", containers: "num",
  };
  const sortedGroups = useMemo(() => {
    let groups = data?.groups || [];
    // When a vertical tab is active, re-scope each period's totals to that vertical.
    if (verticalTab !== "all") {
      groups = groups
        .map((g) => {
          const v = (g.by_vertical || []).find((x) => (x.vertical_id || "__unassigned__") === verticalTab);
          return {
            ...g,
            total_count: v?.total_count || 0,
            total_sales_usd: v?.total_sales_usd || 0,
            total_sales_inr: v?.total_sales_inr || 0,
            containers: v?.containers || 0,
            dispatched_count: v?.dispatched_count ?? 0,
            not_dispatched_count: v?.not_dispatched_count ?? 0,
            avg_lead_time_days: null,
            _scoped: true,
          };
        })
        .filter((g) => g.total_count > 0);
    }
    if (!groupSort.key) return groups;
    const type = GROUP_SORT_TYPES[groupSort.key] || "num";
    const dir = groupSort.dir === "asc" ? 1 : -1;
    const norm = (g) => {
      const v = g[groupSort.key];
      if (v == null || v === "") return null;
      if (type === "num") { const n = Number(v); return isNaN(n) ? null : n; }
      return String(v).toLowerCase(); // period keys 'YYYY-MM' / 'YYYY-Www' sort chronologically as strings
    };
    return [...groups].sort((a, b) => {
      const av = norm(a), bv = norm(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [data, groupSort, verticalTab]);
  const toggleGroupSort = (key) => setGroupSort((s) =>
    s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: "asc" });
  const GroupSortHeader = ({ column, label, align = "left" }) => (
    <th className={`px-3 py-2 font-medium text-${align}`}>
      <button
        type="button"
        onClick={() => toggleGroupSort(column)}
        className={`inline-flex items-center gap-1 hover:text-indigo-600 transition-colors ${groupSort.key === column ? "text-indigo-700" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}
        data-testid={`mgmt-group-sort-${column}`}
        title={`Sort by ${label}`}
      >
        {label}
        {groupSort.key === column
          ? (groupSort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
          : <ChevronsUpDown className="w-3 h-3 opacity-30" />}
      </button>
    </th>
  );
  const monthFilterLabel = (data?.groups || []).find((g) => g.period === monthFilter)?.label || monthFilter;

  // Financial-year-to-date rollup by vertical (Indian FY: Apr–Mar), from the period groups.
  const fyInfo = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear(); const m = now.getMonth() + 1;
    const sy = m >= 4 ? y : y - 1;
    return { start: `${sy}-04`, end: `${sy + 1}-03`, label: `FY ${sy}-${String((sy + 1) % 100).padStart(2, "0")}` };
  }, []);
  const ytd = useMemo(() => {
    const acc = {}; let gInr = 0, gUsd = 0, gCont = 0;
    (data?.groups || []).forEach((g) => {
      if (!/^\d{4}-\d{2}$/.test(g.period)) return;
      if (g.period < fyInfo.start || g.period > fyInfo.end) return;
      (g.by_vertical || []).forEach((v) => {
        const k = v.vertical_id || "__unassigned__";
        const a = acc[k] || (acc[k] = { vertical_id: v.vertical_id, name: v.name, color: v.color, inr: 0, usd: 0, containers: 0 });
        a.inr += v.total_sales_inr || 0; a.usd += v.total_sales_usd || 0; a.containers += v.containers || 0;
      });
      gInr += g.total_sales_inr || 0; gUsd += g.total_sales_usd || 0; gCont += g.containers || 0;
    });
    const items = Object.values(acc).sort((a, b) => (a.vertical_id === null) - (b.vertical_id === null) || (a.name || "").localeCompare(b.name || ""));
    return { items, gInr, gUsd, gCont };
  }, [data, fyInfo]);

  // Chart data — vertical-aware. When "All", stacked ₹ bars per vertical; when a
  // vertical tab is active, a single ₹ series scoped to that vertical. USD line follows.
  const chartVerticals = data?.summary?.by_vertical || [];
  const chartData = useMemo(() => {
    const gs = [...(data?.groups || [])].reverse();
    return gs.map((g) => {
      const row = { label: g.label };
      if (verticalTab === "all") {
        let tot = 0;
        (g.by_vertical || []).forEach((v) => {
          const val = v.total_sales_inr || 0;
          row[`v_${v.vertical_id || "unassigned"}`] = val;
          tot += val;
        });
        row.inrTotal = tot;
        row.usd = g.total_sales_usd;
        row.containers = g.containers || 0;
      } else {
        const v = (g.by_vertical || []).find((x) => (x.vertical_id || "__unassigned__") === verticalTab);
        row.inr = v?.total_sales_inr || 0;
        row.inrTotal = row.inr;
        row.usd = v?.total_sales_usd || 0;
        row.containers = v?.containers || 0;
      }
      return row;
    });
  }, [data, verticalTab]);

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload || {};
    return (
      <div className="bg-white border border-zinc-200 rounded-lg shadow-lg px-3 py-2 text-xs" data-testid="mgmt-chart-tooltip">
        <div className="font-semibold text-zinc-700 mb-1">{label}</div>
        {payload.filter((p) => p.value != null && p.value !== 0).map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-4">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.stroke }} />
              {p.dataKey === "usd" ? "Order Value $" : p.dataKey === "inr" ? "Order Value ₹" : p.name}
            </span>
            <span className="font-medium tabular-nums">{p.dataKey === "usd" ? fmtUSD(p.value) : fmtINR(p.value)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between gap-4 mt-1 pt-1 border-t border-zinc-100 text-zinc-600">
          <span className="inline-flex items-center gap-1.5"><Container className="w-3 h-3" /> Containers</span>
          <span className="font-medium tabular-nums">{row.containers || 0}</span>
        </div>
      </div>
    );
  };
  const tallyForPeriod = (p) => tally?.has_import ? tally.months?.[p] : null;

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Management Report" }]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Purchase Orders", to: "/safety/po", depth: 1 },
        { label: "Shipping Bills", to: "/safety/shipping-bills", depth: 1 },
        { label: "Management Report", active: true, depth: 1 },
      ]}
    >
      <Toaster richColors position="top-right" />
      <div data-testid="management-report-page">
        {/* Header */}
        <div className="sf-surface p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            <div>
              <h1 className="text-xl font-bold tracking-tight">Management Report</h1>
              <p className="text-xs text-zinc-500">
                Sales Price $ &amp; Rs. are taken from each PO's <b>shipping bill</b> (INV AMT for $, FOB VALUE for ₹) when
                available; otherwise the PO's own prices are used. One shipping bill covering multiple POs shows a single merged amount.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading} data-testid="mgmt-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button size="sm" onClick={exportCsv} disabled={!data?.rows?.length} data-testid="mgmt-export-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
            {canReconcile && (
              <>
                <input ref={tallyFileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onTallyFile} data-testid="mgmt-tally-file-input" />
                <Button variant="outline" size="sm" onClick={() => tallyFileRef.current?.click()} disabled={tallyBusy} data-testid="mgmt-tally-upload-btn" title="Upload the Tally sales ledger export to reconcile against the ERP">
                  <FileSpreadsheet className={`w-4 h-4 mr-1.5 text-emerald-600 ${tallyBusy ? "animate-pulse" : ""}`} /> {tallyBusy ? "Reconciling…" : (tally?.has_import ? "Re-upload Tally" : "Reconcile with Tally")}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Tally reconciliation banner */}
        {tally?.has_import && (
          <div className="sf-surface px-4 py-2.5 mb-4 flex items-center gap-3 text-xs flex-wrap" data-testid="mgmt-tally-banner">
            <FileSpreadsheet className="w-4 h-4 text-emerald-600 shrink-0" />
            <span className="text-zinc-600">
              Tally: <b className="text-zinc-800">{tally.import?.file_name}</b> · {tally.import?.invoice_count} invoices
              <span className="text-zinc-400"> (uploaded by {tally.import?.uploaded_by})</span>
            </span>
            <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> {tally.summary.matched} matched</span>
            {tally.summary.mismatched > 0 && <span className="inline-flex items-center gap-1 text-red-600 font-medium"><XCircle className="w-3.5 h-3.5" /> {tally.summary.mismatched} mismatched</span>}
            {tally.summary.missing_in_erp > 0 && <span className="inline-flex items-center gap-1 text-amber-600 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> {tally.summary.missing_in_erp} not in ERP</span>}
            {tally.summary.missing_in_tally > 0 && <span className="inline-flex items-center gap-1 text-orange-600 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> {tally.summary.missing_in_tally} not in Tally</span>}
            <button className="inline-flex items-center gap-1 text-indigo-600 hover:underline font-medium" onClick={() => setTallyPanel({ open: true, period: null })} data-testid="mgmt-tally-view-errors">
              View details
            </button>
            <a className="inline-flex items-center gap-1 text-indigo-600 hover:underline" href={`${API}/management/tally/export.csv?token=${localStorage.getItem("token")}`} target="_blank" rel="noreferrer" data-testid="mgmt-tally-export">
              <Download className="w-3 h-3" /> Export CSV
            </a>
            {canReconcile && (
              <button className="inline-flex items-center gap-1 text-zinc-400 hover:text-red-600 ml-auto" onClick={clearTally} data-testid="mgmt-tally-clear" title="Remove Tally data"><X className="w-3.5 h-3.5" /> Clear</button>
            )}
          </div>
        )}

        {/* Rate banner */}
        {data && (
          <div className="sf-surface px-4 py-2.5 mb-4 flex items-center gap-3 text-xs text-zinc-600 flex-wrap" data-testid="mgmt-rate-banner">
            <Ship className="w-3.5 h-3.5 text-zinc-400" />
            {data.latest_exchange_rate ? (
              <span>
                Fallback rate for undispatched orders: <b className="font-medium text-zinc-800">1 USD = ₹{data.latest_exchange_rate}</b>
                {data.latest_rate_sb ? ` (from latest Shipping Bill ${data.latest_rate_sb})` : ""}
              </span>
            ) : (
              <span className="text-amber-700">No shipping bill with an exchange rate yet — Sales Price Rs. cannot be computed until one is available.</span>
            )}
          </div>
        )}

        {/* Summary cards */}
        {data?.summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="mgmt-summary">
            <div className="sf-surface p-3">
              <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1"><DollarSign className="w-3.5 h-3.5" /> Total Order Value ($)</div>
              <div className="text-lg font-bold tabular-nums" data-testid="mgmt-total-usd">{fmtUSD(data.summary.total_sales_usd)}</div>
            </div>
            <div className="sf-surface p-3">
              <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1"><IndianRupee className="w-3.5 h-3.5" /> Total Sales (₹)</div>
              <div className="text-lg font-bold tabular-nums" data-testid="mgmt-total-inr">{fmtINR(data.summary.total_sales_inr)}</div>
              {(data.summary.domestic_inr || 0) > 0 && (
                <div className="text-[10px] text-emerald-600 mt-0.5" data-testid="mgmt-domestic-hint">incl. {fmtINR(data.summary.domestic_inr)} domestic</div>
              )}
            </div>
            <div className="sf-surface p-3">
              <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1"><Truck className="w-3.5 h-3.5 text-emerald-500" /> Dispatched</div>
              <div className="text-lg font-bold tabular-nums text-emerald-600" data-testid="mgmt-count-dispatched">{data.summary.dispatched_count}</div>
            </div>
            <div className="sf-surface p-3">
              <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1"><CircleDot className="w-3.5 h-3.5 text-zinc-400" /> Pending Dispatch</div>
              <div className="text-lg font-bold tabular-nums text-zinc-700" data-testid="mgmt-count-pending">{data.summary.not_dispatched_count}</div>
            </div>
            <div className="sf-surface p-3">
              <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1"><Clock className="w-3.5 h-3.5 text-indigo-500" /> Avg Lead Time</div>
              <div className="text-lg font-bold tabular-nums" data-testid="mgmt-avg-lead">
                {data.summary.avg_lead_time_days != null ? `${data.summary.avg_lead_time_days} days` : "—"}
              </div>
            </div>
          </div>
        )}

        {data?.summary?.by_vertical?.length > 0 && (
          <div className="sf-surface p-4 mb-4" data-testid="mgmt-verticals-overview">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-zinc-700">Verticals</h2>
              {data.awaiting_invoice?.length > 0 && (
                <button
                  onClick={() => setAwaitingOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:underline"
                  data-testid="mgmt-awaiting-toggle"
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {data.awaiting_invoice.length} shipping bill{data.awaiting_invoice.length > 1 ? "s" : ""} awaiting invoice
                </button>
              )}
            </div>
            {ytd.items.length > 0 && (
              <div className="mb-3 rounded-xl bg-gradient-to-r from-indigo-900 to-indigo-600 text-white p-3" data-testid="mgmt-ytd-strip">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs font-bold uppercase tracking-wide text-indigo-100">{fyInfo.label} to date <span className="normal-case font-normal text-indigo-200">· by {dateBasis === "dispatch" ? "dispatch date" : "order date"} · dispatched-in-FY only</span></div>
                  <div className="text-xs text-indigo-100">
                    Total <span className="font-bold text-white">{fmtINR(ytd.gInr)}</span>
                    {ytd.gUsd ? <> · <span className="font-bold text-white">{fmtUSD(ytd.gUsd)}</span></> : null}
                    <> · <span className="font-bold text-white">{ytd.gCont}</span> containers</>
                  </div>
                </div>
                <div className="flex items-stretch gap-2 mt-2 flex-wrap">
                  {ytd.items.map((v) => (
                    <div key={v.vertical_id || "__unassigned__"} className="flex-1 min-w-[150px] rounded-lg bg-white/10 px-3 py-2" data-testid={`mgmt-ytd-${v.vertical_id || "__unassigned__"}`}>
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-50 truncate">
                        <span className="w-2 h-2 rounded-full" style={{ background: v.color || "#c7d2fe" }} />{v.name}
                      </div>
                      <div className="text-base font-bold tabular-nums leading-tight mt-0.5">{fmtINR(v.inr)}</div>
                      <div className="text-[11px] text-indigo-100 flex items-center gap-2">
                        <span>{v.usd ? fmtUSD(v.usd) : "₹ only"}</span>
                        <span className="inline-flex items-center gap-0.5"><Container className="w-3 h-3" />{v.containers}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {data.summary.by_vertical.map((v) => {
                const key = v.vertical_id || "__unassigned__";
                const active = verticalTab === key;
                return (
                  <button
                    key={key}
                    onClick={() => setVerticalTab(active ? "all" : key)}
                    className={`text-left rounded-xl border p-3 transition-all ${active ? "border-indigo-400 ring-2 ring-indigo-100 bg-indigo-50/40" : "border-zinc-200 hover:border-zinc-300"}`}
                    data-testid={`mgmt-vertical-card-${key}`}
                  >
                    <div className="h-1 w-10 rounded mb-2" style={{ background: v.color || "#a1a1aa" }} />
                    <div className="text-xs font-bold text-zinc-700 truncate">{v.name}</div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 mt-1">Orders on Hand</div>
                    <div className="text-lg font-bold tabular-nums leading-tight">{fmtINR(v.orders_on_hand_inr || 0)}</div>
                    <div className="text-xs text-zinc-500">{v.orders_on_hand_usd ? fmtUSD(v.orders_on_hand_usd) : "₹ only"}</div>
                    <div className="text-[11px] text-zinc-400 mt-1 flex items-center gap-2">
                      <span>{v.not_dispatched_count || 0} pending order{(v.not_dispatched_count || 0) !== 1 ? "s" : ""}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Awaiting-invoice watch-list */}
            {awaitingOpen && data.awaiting_invoice?.length > 0 && (
              <div className="mt-3 border border-amber-200 rounded-xl overflow-hidden" data-testid="mgmt-awaiting-panel">
                <div className="bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Shipping bills with no matching commercial invoice yet (not in totals — classified best-effort by destination/consignee)
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 text-zinc-500">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-medium">SB No.</th>
                      <th className="px-3 py-1.5 text-left font-medium">SB Date</th>
                      <th className="px-3 py-1.5 text-left font-medium">Consignee</th>
                      <th className="px-3 py-1.5 text-left font-medium">Destination</th>
                      <th className="px-3 py-1.5 text-left font-medium">Vertical (guess)</th>
                      <th className="px-3 py-1.5 text-right font-medium">FOB ₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.awaiting_invoice.map((a) => (
                      <tr key={a.sb_id} className="border-t border-zinc-100" data-testid={`mgmt-awaiting-row-${a.sb_no}`}>
                        <td className="px-3 py-1.5 font-mono">{a.sb_no}</td>
                        <td className="px-3 py-1.5">{a.sb_date || "—"}</td>
                        <td className="px-3 py-1.5">{a.consignee_name || <span className="text-zinc-400">unknown</span>}</td>
                        <td className="px-3 py-1.5">{a.country_of_destination || "—"}{a.port_of_discharge ? ` · ${a.port_of_discharge}` : ""}</td>
                        <td className="px-3 py-1.5">{a.vertical_name || <span className="text-zinc-400">unresolved</span>}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{a.fob_value_inr != null ? fmtINR(a.fob_value_inr) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Vertical tab bar */}
            <div className="flex items-center gap-1.5 mt-3 flex-wrap border-t border-zinc-100 pt-3">
              <span className="text-xs text-zinc-500 mr-1">View:</span>
              <button
                onClick={() => setVerticalTab("all")}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium ${verticalTab === "all" ? "bg-indigo-600 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
                data-testid="mgmt-vtab-all"
              >All verticals</button>
              {data.summary.by_vertical.map((v) => {
                const key = v.vertical_id || "__unassigned__";
                return (
                  <button
                    key={key}
                    onClick={() => setVerticalTab(key)}
                    className={`text-xs px-3 py-1.5 rounded-lg font-medium inline-flex items-center gap-1.5 ${verticalTab === key ? "bg-indigo-600 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
                    data-testid={`mgmt-vtab-${key}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ background: v.color || "#a1a1aa" }} />
                    {v.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Filter + table */}
        <div className="sf-surface p-4">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-xs text-zinc-500 mr-1">Dispatch status:</span>
            {[
              { k: "all", label: "All" },
              { k: "dispatched", label: "Dispatched" },
              { k: "not_dispatched", label: "Not Dispatched" },
            ].map((f) => (
              <Button
                key={f.k}
                variant={filter === f.k ? "default" : "outline"}
                size="sm"
                className="h-8"
                onClick={() => setFilter(f.k)}
                data-testid={`mgmt-filter-${f.k}`}
              >
                {f.label}
              </Button>
            ))}
            <div className="flex items-center gap-1.5 ml-2">
              <span className="text-xs text-zinc-500">PO Date:</span>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-[9.5rem]" data-testid="mgmt-date-from" />
              <span className="text-xs text-zinc-400">to</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-[9.5rem]" data-testid="mgmt-date-to" />
              {(dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="mgmt-date-clear">Clear</Button>
              )}
            </div>
            <span className="text-xs text-zinc-500 ml-auto">{data?.count || 0} PO{(data?.count || 0) === 1 ? "" : "s"}</span>
          </div>

          {/* Group-by toggle */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-xs text-zinc-500 mr-1">Summary by:</span>
            {[
              { k: "none", label: "None (list)" },
              { k: "month", label: "Month" },
              { k: "week", label: "Week" },
            ].map((g) => (
              <Button
                key={g.k}
                variant={groupBy === g.k ? "default" : "outline"}
                size="sm"
                className="h-8"
                onClick={() => setGroupBy(g.k)}
                data-testid={`mgmt-group-${g.k}`}
              >
                {g.label}
              </Button>
            ))}
            {groupBy !== "none" && (
              <div className="flex items-center gap-2 ml-3">
                <span className="text-xs text-zinc-500 mr-1">By date:</span>
                {[
                  { k: "po", label: "Order date" },
                  { k: "dispatch", label: "Dispatch date" },
                ].map((b) => (
                  <Button
                    key={b.k}
                    variant={dateBasis === b.k ? "default" : "outline"}
                    size="sm"
                    className="h-8"
                    onClick={() => setDateBasis(b.k)}
                    data-testid={`mgmt-basis-${b.k}`}
                  >
                    {b.label}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Grouped (month/week) summary */}
          {groupBy !== "none" && data?.groups && !loading && (
            <>
              {data.groups.length > 0 && (
                <div className="rounded border bg-white p-4 mb-4" data-testid="mgmt-chart">
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <div className="text-xs text-zinc-500">
                      Order Value by {groupBy === "month" ? "month" : "week"} · {dateBasis === "dispatch" ? "dispatch date" : "order date"}
                      <span className="ml-2 text-zinc-400">(₹ = bars, left · $ = line, right)</span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={chartData} margin={{ top: 28, right: 14, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis yAxisId="inr" tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCompact(v, "inr")} width={66} />
                      <YAxis yAxisId="usd" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCompact(v, "usd")} width={52} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} formatter={(val) => (val === "usd" ? "Order Value $ (line)" : val === "inr" ? "Order Value ₹ (bars)" : val)} />
                      {verticalTab === "all" ? (
                        chartVerticals.map((v, i) => (
                          <Bar
                            key={v.vertical_id || "unassigned"}
                            yAxisId="inr"
                            dataKey={`v_${v.vertical_id || "unassigned"}`}
                            name={v.name}
                            stackId="inr"
                            fill={v.color || "#a1a1aa"}
                            radius={i === chartVerticals.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                            maxBarSize={44}
                          >
                            {i === 0 && (
                              <LabelList dataKey="containers" position="insideBottom" offset={6} formatter={(v) => (v ? `${v} cntr` : "")} style={{ fontSize: 9, fill: "#ffffff", fontWeight: 700, stroke: "#334155", strokeWidth: 2.5, paintOrder: "stroke" }} />
                            )}
                            {i === chartVerticals.length - 1 && (
                              <LabelList dataKey="inrTotal" position="top" offset={6} formatter={(v) => (v ? fmtCompact(v, "inr") : "")} style={{ fontSize: 9, fill: "#0f172a", fontWeight: 700, stroke: "#ffffff", strokeWidth: 3, paintOrder: "stroke" }} />
                            )}
                          </Bar>
                        ))
                      ) : (
                        <Bar yAxisId="inr" dataKey="inr" name="inr" fill="#0d9488" radius={[4, 4, 0, 0]} maxBarSize={44}>
                          <LabelList dataKey="inr" position="insideTop" offset={6} formatter={(v) => fmtCompact(v, "inr")} style={{ fontSize: 9, fill: "#ffffff", fontWeight: 700 }} />
                          <LabelList dataKey="containers" position="insideBottom" offset={6} formatter={(v) => (v ? `${v} cntr` : "")} style={{ fontSize: 9, fill: "#ffffff", fontWeight: 700, stroke: "#334155", strokeWidth: 2.5, paintOrder: "stroke" }} />
                        </Bar>
                      )}
                      <Line yAxisId="usd" type="monotone" dataKey="usd" name="usd" stroke="#4f46e5" strokeWidth={2} dot={{ r: 3, fill: "#4f46e5" }}>
                        <LabelList dataKey="usd" position="top" offset={10} formatter={(v) => fmtCompact(v, "usd")} style={{ fontSize: 9, fill: "#4f46e5", fontWeight: 700, stroke: "#ffffff", strokeWidth: 3, paintOrder: "stroke" }} />
                      </Line>
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
              {groupBy === "month" && (() => {
                const months = data.groups.filter((g) => isMonthKey(g.period));
                const done = months.filter((g) => { const rc = recon[g.period]; return rc && (rc.status === "interim" || rc.status === "full"); }).length;
                const drift = months.filter((g) => { const rc = recon[g.period]; return rc && (rc.drift || rc.new_since_cutoff > 0); }).length;
                return (
                  <div className="flex items-center gap-3 flex-wrap mb-2 text-sm" data-testid="mgmt-recon-summary">
                    <span className="inline-flex items-center gap-1.5 font-medium text-zinc-700"><ShieldCheck className="w-4 h-4 text-emerald-600" /> {done} of {months.length} months reconciled</span>
                    {drift > 0 && <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium"><AlertTriangle className="w-3.5 h-3.5" /> {drift} need re-verify</span>}
                    <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-zinc-500 cursor-pointer">
                      <input type="checkbox" checked={showUnreconciledOnly} onChange={(e) => setShowUnreconciledOnly(e.target.checked)} data-testid="mgmt-recon-filter-toggle" /> Show unreconciled only
                    </label>
                  </div>
                );
              })()}
              <div className="overflow-x-auto rounded border bg-white mb-4" data-testid="mgmt-groups-table">
              <table className="w-full text-sm">
                <thead className="bg-indigo-50 text-zinc-600">
                  <tr>
                    <GroupSortHeader column="period" label={groupBy === "month" ? "Month" : "Week"} />
                    <GroupSortHeader column="total_count" label="POs" align="right" />
                    <GroupSortHeader column="dispatched_count" label="Dispatched" align="right" />
                    <GroupSortHeader column="not_dispatched_count" label="Pending" align="right" />
                    <GroupSortHeader column="containers" label="Containers" align="right" />
                    <GroupSortHeader column="total_sales_usd" label="Order Value $" align="right" />
                    <GroupSortHeader column="total_sales_inr" label="Order Value ₹" align="right" />
                    <GroupSortHeader column="avg_lead_time_days" label="Avg Lead (days)" align="right" />
                    {groupBy === "month" && <th className="px-3 py-2 text-center font-medium">Reconciliation</th>}
                    {groupBy === "month" && tally?.has_import && <th className="px-3 py-2 text-center font-medium">Tally</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedGroups.filter((g) => {
                    if (groupBy === "month" && showUnreconciledOnly) {
                      const rc = recon[g.period];
                      return !(rc && (rc.status === "interim" || rc.status === "full"));
                    }
                    return true;
                  }).map((g) => {
                    const rc = recon[g.period];
                    const active = rc && (rc.status === "interim" || rc.status === "full");
                    return (
                    <tr key={g.period} className="border-t hover:bg-zinc-50" data-testid={`mgmt-group-row-${g.period}`}>
                      <td className="px-3 py-2 font-medium">
                        {isMonthKey(g.period) ? (
                          <button
                            type="button"
                            className={`inline-flex items-center gap-1 hover:underline ${monthFilter === g.period ? "text-indigo-700 font-semibold" : "text-zinc-800 hover:text-indigo-600"}`}
                            onClick={() => setMonthFilter(monthFilter === g.period ? null : g.period)}
                            data-testid={`mgmt-month-filter-${g.period}`}
                            title="Filter the PO list below to this month"
                          >
                            <Filter className="w-3 h-3 opacity-60" /> {g.label}
                          </button>
                        ) : g.label}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{g.total_count}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-600">{g.dispatched_count != null ? g.dispatched_count : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{g.not_dispatched_count != null ? g.not_dispatched_count : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums" data-testid={`mgmt-group-containers-${g.period}`}>
                        <span className="inline-flex items-center gap-1 justify-end"><Container className="w-3.5 h-3.5 text-zinc-400" />{g.containers != null ? g.containers : 0}</span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtUSD(g.total_sales_usd)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtINR(g.total_sales_inr)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-600">{g.avg_lead_time_days != null ? g.avg_lead_time_days : "—"}</td>
                      {groupBy === "month" && (
                        <td className="px-3 py-2 text-center">
                          {!isMonthKey(g.period) ? (
                            <span className="text-xs text-zinc-300">—</span>
                          ) : active ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${rc.status === "full" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} title={rc.note || ""} data-testid={`mgmt-recon-badge-${g.period}`}>
                                <CheckCircle2 className="w-3 h-3" /> {rc.status === "full" ? "Reconciled" : `Through ${rc.cutoff_date}`}
                              </span>
                              <span className="text-[10px] text-zinc-400">by {rc.reconciled_by_name || rc.reconciled_by}</span>
                              {(rc.drift || rc.new_since_cutoff > 0) && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 font-medium" data-testid={`mgmt-recon-drift-${g.period}`}>
                                  <AlertTriangle className="w-2.5 h-2.5" /> {rc.new_since_cutoff > 0 ? `${rc.new_since_cutoff} new since cutoff` : "figures changed — re-verify"}
                                </span>
                              )}
                              {canReconcile && (
                                <div className="flex gap-2 mt-0.5">
                                  {(rc.status === "interim" || rc.drift || rc.new_since_cutoff > 0) && (
                                    <button className="text-[10px] text-indigo-600 hover:underline" onClick={() => openReconcile(g)} data-testid={`mgmt-recon-topup-${g.period}`}>top-up</button>
                                  )}
                                  <button className="text-[10px] text-zinc-500 hover:underline" onClick={() => reopenMonth(g)} data-testid={`mgmt-recon-reopen-${g.period}`}>reopen</button>
                                </div>
                              )}
                            </div>
                          ) : canReconcile ? (
                            <button className="inline-flex items-center gap-1 text-xs border border-indigo-200 text-indigo-700 rounded px-2 py-1 hover:bg-indigo-50" onClick={() => openReconcile(g)} data-testid={`mgmt-recon-mark-${g.period}`}>
                              <ShieldCheck className="w-3 h-3" /> Mark reconciled
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-zinc-400" data-testid={`mgmt-recon-pending-${g.period}`}><CircleDot className="w-3 h-3" /> Pending</span>
                          )}
                        </td>
                      )}
                      {groupBy === "month" && tally?.has_import && (
                        <td className="px-3 py-2 text-center">
                          {(() => {
                            const tm = tallyForPeriod(g.period);
                            if (!tm) return <span className="text-xs text-zinc-300">—</span>;
                            if (tm.all_matched) return (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid={`mgmt-tally-badge-${g.period}`} title="All invoices in this month match the Tally ledger to the rupee">
                                <CheckCircle2 className="w-3 h-3" /> Reconciled with Tally Data
                              </span>
                            );
                            if (tm.all_clear) return (
                              <button className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200" onClick={() => setTallyPanel({ open: true, period: g.period })} data-testid={`mgmt-tally-badge-${g.period}`} title="All discrepancies resolved with notes — click to review">
                                <CheckCircle2 className="w-3 h-3" /> Reconciled (with notes)
                              </button>
                            );
                            return (
                              <button className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200" onClick={() => setTallyPanel({ open: true, period: g.period })} data-testid={`mgmt-tally-badge-${g.period}`} title="Click to view and resolve the mismatched / unmatched invoices for this month">
                                <AlertTriangle className="w-3 h-3" /> {tm.unresolved} issue{tm.unresolved === 1 ? "" : "s"}
                              </button>
                            );
                          })()}
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </>
          )}

          {monthFilter && (
            <div className="flex items-center gap-2 mb-2 text-sm" data-testid="mgmt-month-filter-chip">
              <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 rounded-full px-3 py-1 text-xs font-medium">
                <Filter className="w-3 h-3" /> Showing {monthFilterLabel} ({dateBasis === "dispatch" ? "by dispatch date" : "by PO date"}) · {visibleRows.length} PO{visibleRows.length === 1 ? "" : "s"}
                <button onClick={() => setMonthFilter(null)} className="ml-1 hover:text-indigo-900" data-testid="mgmt-month-filter-clear" title="Clear month filter"><X className="w-3 h-3" /></button>
              </span>
            </div>
          )}
          <div className="overflow-x-auto rounded border bg-white">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" />
              </div>
            ) : !canView ? (
              <div className="p-8 text-center text-zinc-500 text-sm">You do not have permission to view this report.</div>
            ) : !data?.rows?.length ? (
              <div className="p-10 text-center text-zinc-500">
                <BarChart3 className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No purchase orders found for this filter.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-600">
                  <tr>
                    <SortHeader column="sl_no" label="Sl.No." />
                    <SortHeader column="po_number" label="P.O.No." />
                    <SortHeader column="po_date" label="P.O.Date" />
                    <SortHeader column="sales_price_usd" label="Sales Price $" align="right" />
                    <SortHeader column="sales_price_inr" label="Sales Price Rs." align="right" />
                    <SortHeader column="exchange_rate" label="FX Rate" align="right" />
                    <SortHeader column="required_date" label="Required Date" />
                    <SortHeader column="actual_dispatch_date" label="Actual Dispatch Date" />
                    <SortHeader column="invoice_sb" label="Invoice / SB No." />
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => (
                    <tr key={r.po_number + r.sl_no} className="border-t hover:bg-zinc-50" data-testid={`mgmt-row-${r.po_number}`}>
                      <td className="px-3 py-2 text-zinc-500">{r.sl_no}</td>
                      <td className="px-3 py-2 font-mono font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {r.vertical_color && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: r.vertical_color }} title={r.vertical_name || ""} data-testid={`mgmt-row-vertical-${r.sl_no}`} />}
                          <CircleDot className={`w-3 h-3 ${r.dispatched ? "text-emerald-500" : "text-zinc-300"}`} />
                          {r.no_po ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700" data-testid={`mgmt-nopo-badge-${r.sl_no}`}>No PO</span>
                              <span className="text-zinc-500 text-xs normal-case font-sans" title={r.customer_name || ""}>{r.customer_name ? r.customer_name.slice(0, 22) : "—"}</span>
                            </span>
                          ) : r.po_id ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/safety/po/${r.po_id}/detail`)}
                              className="text-indigo-600 hover:text-indigo-800 hover:underline"
                              data-testid={`mgmt-po-link-${r.po_number}`}
                              title="Open PO detail"
                            >
                              {r.po_number || "—"}
                            </button>
                          ) : (
                            r.po_number || "—"
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2">{fmtDate(r.po_date)}</td>
                      <td className="px-3 py-2 text-right tabular-nums align-middle" data-testid={`mgmt-usd-${r.po_number}`}>
                        <div className="flex flex-col items-end gap-1">
                          <span>{fmtUSD(r.sales_price_usd)}</span>
                          {r.price_source === "shipping_bill" ? (
                            <span
                              className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${r.price_estimated ? "bg-amber-100 text-amber-700" : "bg-teal-100 text-teal-700"}`}
                              data-testid={`mgmt-price-source-${r.po_number}`}
                              title={r.price_estimated
                                ? `Estimated from shipping bill FOB (₹) ÷ exchange rate — the bill had no parsed INV AMT.${r.sb_count > 1 ? ` · ${r.sb_count} shipping bills` : ""}`
                                : `From shipping bill (INV AMT for $, FOB VALUE for ₹)${r.sb_count > 1 ? ` · ${r.sb_count} shipping bills summed` : ""}`}
                            >
                              {r.price_estimated ? (r.sb_count > 1 ? `SB≈ · ${r.sb_count}` : "SB≈") : (r.sb_count > 1 ? `SB · ${r.sb_count} bills` : "SB")}
                            </span>
                          ) : (
                            <span
                              className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500"
                              data-testid={`mgmt-price-source-${r.po_number}`}
                              title="From PO line prices"
                            >
                              PO
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums align-middle" data-testid={`mgmt-inr-${r.po_number}`} title={r.rate_source ? `Rate: ${r.exchange_rate} (${r.rate_source})` : ""}>
                        {fmtINR(r.sales_price_inr)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-600 whitespace-nowrap" data-testid={`mgmt-fx-${r.po_number}`}>
                        {r.exchange_rate ? (
                          <span title={r.rate_source || ""}>
                            ₹{r.exchange_rate}/$1
                            {r.rate_source && r.rate_source.startsWith("latest") && <span className="text-amber-500 text-xs ml-1">*</span>}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2">{fmtDate(r.required_date)}</td>
                      <td className="px-3 py-2">
                        {r.dispatched ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700" data-testid={`mgmt-dispatch-${r.po_number}`}>
                            {r.actual_dispatch_date}
                          </span>
                        ) : (
                          <span className="text-zinc-400">Not dispatched</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.dispatched && r.shipping_bills?.length ? (
                          <div className="flex flex-col gap-1">
                            {r.shipping_bills.map((sb, idx) => (
                              sb.sb_id && canViewPL ? (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => setPreview({ open: true, sbId: sb.sb_id, plId: sb.packing_list_id || null, view: "sb", title: `${sb.invoice_no || ""}${sb.sb_no ? ` · SB ${sb.sb_no}` : ""}` })}
                                  className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-800 hover:underline"
                                  data-testid={idx === 0 ? `mgmt-invoice-${r.po_number}` : `mgmt-invoice-${r.po_number}-${idx}`}
                                  title="Preview shipping bill PDF"
                                >
                                  <FileText className="w-3.5 h-3.5 shrink-0" />
                                  <span className="font-mono text-xs">{sb.invoice_no || "—"}</span>
                                  {sb.sb_no && <span className="text-zinc-400 text-xs">· SB {sb.sb_no}</span>}
                                  {sb.shared_po_count > 1 && (
                                    <span
                                      className="text-[9px] px-1 rounded bg-amber-100 text-amber-700"
                                      title={`This invoice/packing list lists ${(sb.linked_pos || []).length || "multiple"} PO number(s): ${(sb.linked_pos || []).join(", ")}${(sb.shared_with || []).length ? ` — also counted on PO ${sb.shared_with.join(", ")}` : ""}`}
                                    >
                                      shared{(sb.shared_with || []).length ? `: ${sb.shared_with.join(", ")}` : ""}
                                    </span>
                                  )}
                                </button>
                              ) : (
                                <span key={idx} className="font-mono text-xs text-zinc-600">
                                  {sb.invoice_no || "—"}{sb.sb_no ? ` · SB ${sb.sb_no}` : ""}
                                </span>
                              )
                            ))}
                          </div>
                        ) : (
                          <span className="text-zinc-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {data?.summary && !monthFilter && (
                  <tfoot className="bg-zinc-50 border-t-2 font-semibold" data-testid="mgmt-table-footer">
                    <tr>
                      <td className="px-3 py-2" colSpan={3}>Total ({data.summary.total_count} PO{data.summary.total_count === 1 ? "" : "s"})</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtUSD(data.summary.total_sales_usd)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtINR(data.summary.total_sales_inr)}</td>
                      <td className="px-3 py-2 text-xs text-zinc-500" colSpan={4}>
                        {data.summary.dispatched_count} dispatched · {data.summary.not_dispatched_count} pending
                        {data.summary.avg_lead_time_days != null ? ` · avg lead ${data.summary.avg_lead_time_days}d` : ""}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
          </div>
        </div>

        {(() => {
          const domInv = (domestic?.invoices || []).filter((inv) =>
            verticalTab === "all" ? true : (inv.vertical_id || "__unassigned__") === verticalTab
          );
          if (!domInv.length) return null;
          const totTax = domInv.reduce((s, i) => s + (i.taxable_value || 0), 0);
          const totVal = domInv.reduce((s, i) => s + (i.total_value || 0), 0);
          return (
            <div className="sf-surface p-4 mt-4" data-testid="mgmt-domestic-invoices">
              <div className="flex items-center gap-2 mb-3">
                <IndianRupee className="w-4 h-4 text-emerald-600" />
                <span className="font-medium text-sm">Domestic Sales Invoices (INR){verticalTab !== "all" ? " · this vertical" : ""}</span>
                <span className="text-xs text-zinc-500">({domInv.length})</span>
              </div>
              <div className="overflow-x-auto rounded border bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase">
                    <tr>
                      <th className="px-3 py-2 text-left">Invoice No.</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Customer</th>
                      <th className="px-3 py-2 text-left">Buyer's Order</th>
                      <th className="px-3 py-2 text-right">Taxable</th>
                      <th className="px-3 py-2 text-right">GST</th>
                      <th className="px-3 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {domInv.map((inv) => {
                      const gst = (inv.cgst || 0) + (inv.sgst || 0) + (inv.igst || 0);
                      return (
                        <tr key={inv.id} className="hover:bg-emerald-50 cursor-pointer" onClick={() => openDomView(inv)} data-testid={`mgmt-dom-inv-${inv.id}`}>
                          <td className="px-3 py-2 font-mono text-xs text-emerald-700 underline decoration-dotted">{inv.invoice_no || "—"}</td>
                          <td className="px-3 py-2 text-xs text-zinc-600">{inv.invoice_date || "—"}</td>
                          <td className="px-3 py-2">
                            {inv.customer_name}
                            {!inv.matched_customer && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">unclassified</span>}
                          </td>
                          <td className="px-3 py-2 text-xs text-zinc-500">{inv.po_number || "—"}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtINR(inv.taxable_value)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-xs text-zinc-500">{fmtINR(gst)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{fmtINR(inv.total_value)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-zinc-50 border-t-2 font-semibold">
                    <tr>
                      <td className="px-3 py-2" colSpan={4}>Total ({domInv.length} invoice{domInv.length === 1 ? "" : "s"})</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtINR(totTax)}</td>
                      <td className="px-3 py-2" />
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmtINR(totVal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          );
        })()}
      </div>

      <Dialog open={preview.open} onOpenChange={(o) => setPreview((p) => ({ ...p, open: o }))}>
        <DialogContent className="max-w-4xl w-[92vw]" data-testid="mgmt-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base flex-wrap">
              <Ship className="w-4 h-4 text-indigo-600" />
              <span>{preview.title || "Preview"}</span>
              <div className="flex items-center gap-1 ml-2">
                <Button
                  variant={preview.view === "sb" ? "default" : "outline"}
                  size="sm"
                  className="h-7 px-3 text-xs"
                  onClick={() => setPreview((p) => ({ ...p, view: "sb" }))}
                  data-testid="mgmt-preview-tab-sb"
                >
                  Shipping Bill
                </Button>
                <Button
                  variant={preview.view === "pl" ? "default" : "outline"}
                  size="sm"
                  className="h-7 px-3 text-xs"
                  disabled={!preview.plId}
                  onClick={() => setPreview((p) => ({ ...p, view: "pl" }))}
                  data-testid="mgmt-preview-tab-pl"
                  title={preview.plId ? "View matched packing list" : "No packing list found for this invoice"}
                >
                  Packing List
                </Button>
              </div>
              {(preview.view === "sb" ? preview.sbId : preview.plId) && (
                <a
                  href={preview.view === "sb"
                    ? `${API}/shipping-bills/${preview.sbId}/preview?token=${localStorage.getItem("token")}`
                    : `${API}/packing-lists/${preview.plId}/preview?token=${localStorage.getItem("token")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-auto inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                  data-testid="mgmt-preview-newtab"
                >
                  Open in new tab <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </DialogTitle>
          </DialogHeader>
          {preview.view === "sb" && preview.sbId && (
            <iframe
              title="Shipping Bill Preview"
              src={`${API}/shipping-bills/${preview.sbId}/preview?token=${localStorage.getItem("token")}`}
              className="w-full h-[70vh] rounded border"
              data-testid="mgmt-preview-iframe"
            />
          )}
          {preview.view === "pl" && preview.plId && (
            <iframe
              title="Packing List Preview"
              src={`${API}/packing-lists/${preview.plId}/preview?token=${localStorage.getItem("token")}`}
              className="w-full h-[70vh] rounded border"
              data-testid="mgmt-preview-pl-iframe"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Domestic GST invoice viewer (click-through from drill-in) */}
      <Dialog open={domView.open} onOpenChange={(o) => !o && setDomView({ open: false, inv: null, data: null, loading: false })}>
        <DialogContent className="max-w-3xl w-[92vw] max-h-[85vh] overflow-hidden flex flex-col" data-testid="mgmt-dom-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><IndianRupee className="w-5 h-5 text-emerald-600" /> {domView.data?.invoice_no || domView.inv?.invoice_no || "Invoice"}</DialogTitle>
            <DialogDescription>
              {domView.data?.customer_name}{domView.data?.buyer_gstin ? ` · GSTIN ${domView.data.buyer_gstin}` : ""}
              {domView.data?.po_number ? ` · PO ${domView.data.po_number}` : ""}
              {domView.data?.invoice_date ? ` · ${domView.data.invoice_date}` : ""}
            </DialogDescription>
          </DialogHeader>
          {domView.loading ? (
            <div className="py-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>
          ) : domView.data ? (
            <div className="flex-1 overflow-auto">
              <table className="w-full text-sm mb-4" data-testid="mgmt-dom-view-table">
                <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase sticky top-0">
                  <tr>
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">Description</th>
                    <th className="p-2 text-left">HSN</th>
                    <th className="p-2 text-right">Qty</th>
                    <th className="p-2 text-right">Rate</th>
                    <th className="p-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(domView.data.items || []).map((it) => (
                    <tr key={it.id}>
                      <td className="p-2 text-zinc-400">{it.line_no}</td>
                      <td className="p-2">{it.description}</td>
                      <td className="p-2 font-mono text-xs">{it.hsn || "—"}</td>
                      <td className="p-2 text-right">{it.qty} {it.unit || ""}</td>
                      <td className="p-2 text-right">{fmtINR(it.rate)}</td>
                      <td className="p-2 text-right font-medium">{fmtINR(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="ml-auto max-w-xs text-sm space-y-1" data-testid="mgmt-dom-view-totals">
                <div className="flex justify-between"><span className="text-zinc-500">Taxable value</span><span className="font-medium">{fmtINR(domView.data.totals?.taxable_value)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">CGST</span><span>{fmtINR(domView.data.totals?.cgst)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">SGST</span><span>{fmtINR(domView.data.totals?.sgst)}</span></div>
                {(domView.data.totals?.igst || 0) > 0 && <div className="flex justify-between"><span className="text-zinc-500">IGST</span><span>{fmtINR(domView.data.totals?.igst)}</span></div>}
                {(domView.data.totals?.round_off || 0) !== 0 && <div className="flex justify-between"><span className="text-zinc-500">Round off</span><span>{fmtINR(domView.data.totals?.round_off)}</span></div>}
                <div className="flex justify-between border-t pt-1 font-bold text-emerald-700"><span>Total</span><span data-testid="mgmt-dom-view-total">{fmtINR(domView.data.totals?.total_value)}</span></div>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => downloadDomInvoice(domView.inv)} data-testid="mgmt-dom-view-download">
              <Download className="w-4 h-4 mr-1.5" />Download original invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reconcile month dialog */}
      <Dialog open={reconDlg.open} onOpenChange={(o) => !o && setReconDlg({ open: false, group: null, cutoff: "", note: "", busy: false })}>
        <DialogContent className="max-w-md" data-testid="mgmt-reconcile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Reconcile {reconDlg.group?.label}</DialogTitle>
          </DialogHeader>
          {reconDlg.group && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2 rounded border bg-zinc-50 p-3 text-xs">
                <div>Dispatched POs<div className="text-base font-semibold text-zinc-800">{reconDlg.group.dispatched_count}</div></div>
                <div>Avg lead<div className="text-base font-semibold text-zinc-800">{reconDlg.group.avg_lead_time_days ?? "—"}<span className="text-[10px] font-normal"> d</span></div></div>
                <div>Order value $<div className="text-base font-semibold text-zinc-800">{fmtUSD(reconDlg.group.total_sales_usd)}</div></div>
                <div>Order value ₹<div className="text-base font-semibold text-zinc-800">{fmtINR(reconDlg.group.total_sales_inr)}</div></div>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">Reconcile dispatches up to (cutoff date)</label>
                <input type="date" className="w-full border rounded px-3 py-2 text-sm" value={reconDlg.cutoff} onChange={(e) => setReconDlg((d) => ({ ...d, cutoff: e.target.value }))} data-testid="mgmt-reconcile-cutoff" />
                <p className="text-[11px] text-zinc-500 mt-1">
                  {reconDlg.cutoff && reconDlg.cutoff >= monthEnd(reconDlg.group.period)
                    ? "Covers the full month — will be marked Reconciled."
                    : "Signs off dispatches on/before this date — marked interim (top-up later)."}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">Note (optional)</label>
                <textarea className="w-full border rounded px-3 py-2 text-sm" rows={2} value={reconDlg.note} onChange={(e) => setReconDlg((d) => ({ ...d, note: e.target.value }))} placeholder="e.g. cross-checked against Tally / bank realizations" data-testid="mgmt-reconcile-note" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setReconDlg({ open: false, group: null, cutoff: "", note: "", busy: false })}>Cancel</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={reconDlg.busy} onClick={submitReconcile} data-testid="mgmt-reconcile-submit">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> {reconDlg.busy ? "Saving…" : "Confirm reconciled"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tally reconciliation details panel */}
      <Dialog open={tallyPanel.open} onOpenChange={(o) => !o && setTallyPanel({ open: false, period: null })}>
        <DialogContent className="max-w-5xl w-[94vw]" data-testid="mgmt-tally-panel">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Tally reconciliation
              {tallyPanel.period && <span className="text-sm font-normal text-zinc-500">· {tallyPanel.period}</span>}
            </DialogTitle>
          </DialogHeader>
          {tally?.has_import && (() => {
            const rows = (tally.results || []).filter((r) => !tallyPanel.period || r.period === tallyPanel.period);
            const issues = rows.filter((r) => r.status !== "match");
            const matched = rows.filter((r) => r.status === "match").length;
            const label = (s) => ({ match: "Match", mismatch: "Mismatch", missing_in_erp: "Missing in ERP", missing_in_tally: "Missing in Tally" }[s] || s);
            const cls = (s) => ({ match: "bg-emerald-100 text-emerald-700", mismatch: "bg-red-100 text-red-700", missing_in_erp: "bg-amber-100 text-amber-700", missing_in_tally: "bg-orange-100 text-orange-700" }[s] || "bg-zinc-100 text-zinc-600");
            return (
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-xs flex-wrap">
                  <span className="text-emerald-700 font-medium">{matched} matched</span>
                  <span className="text-zinc-400">·</span>
                  <span className="text-red-600 font-medium">{issues.filter((r) => !r.resolved).length} to resolve</span>
                  {issues.some((r) => r.resolved) && <><span className="text-zinc-400">·</span><span className="text-emerald-600">{issues.filter((r) => r.resolved).length} resolved with notes</span></>}
                  {tallyPanel.period && <button className="text-indigo-600 hover:underline" onClick={() => setTallyPanel({ open: true, period: null })} data-testid="mgmt-tally-panel-all">Show all months</button>}
                  <a className="ml-auto inline-flex items-center gap-1 text-indigo-600 hover:underline" href={`${API}/management/tally/export.csv?token=${localStorage.getItem("token")}`} target="_blank" rel="noreferrer" data-testid="mgmt-tally-panel-export"><Download className="w-3 h-3" /> Export CSV</a>
                </div>
                <div className="overflow-auto max-h-[62vh] rounded border">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-50 text-zinc-500 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left font-medium">Invoice</th>
                        <th className="px-2 py-2 text-left font-medium">Month</th>
                        <th className="px-2 py-2 text-left font-medium">Party</th>
                        <th className="px-2 py-2 text-left font-medium">PO</th>
                        <th className="px-2 py-2 text-right font-medium">Tally ₹ (taxable)</th>
                        <th className="px-2 py-2 text-right font-medium">ERP ₹ (FOB)</th>
                        <th className="px-2 py-2 text-right font-medium">Diff ₹</th>
                        <th className="px-2 py-2 text-center font-medium">Status</th>
                        <th className="px-2 py-2 text-center font-medium">SB</th>
                        {canReconcile && <th className="px-2 py-2 text-center font-medium">Resolve</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(issues.length ? issues : rows).map((r, i) => (
                        <tr key={(r.invoice_no || "") + i} className={`border-t hover:bg-zinc-50 ${r.resolved ? "bg-emerald-50/40" : ""}`} data-testid={`mgmt-tally-row-${r.invoice_no}`}>
                          <td className="px-2 py-1.5 font-mono">{r.invoice_no}</td>
                          <td className="px-2 py-1.5 text-zinc-500">{r.period}</td>
                          <td className="px-2 py-1.5 text-zinc-600 max-w-[160px] truncate" title={r.party || ""}>{r.party || "—"}</td>
                          <td className="px-2 py-1.5 font-mono text-zinc-600">{r.po_number || "—"}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{r.tally_taxable != null ? fmtINR(r.tally_taxable) : "—"}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{r.erp_inr != null ? fmtINR(r.erp_inr) : (r.erp_inr_missing ? <span className="text-amber-600">not parsed</span> : "—")}</td>
                          <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${r.diff ? "text-red-600" : "text-zinc-400"}`}>{r.diff != null ? (r.diff > 0 ? `+${fmtINR(r.diff)}` : fmtINR(r.diff)) : "—"}</td>
                          <td className="px-2 py-1.5 text-center"><span className={`inline-block px-2 py-0.5 rounded-full font-semibold ${cls(r.status)}`}>{label(r.status)}</span></td>
                          <td className="px-2 py-1.5 text-center">
                            {r.sb_id ? (
                              <button className="text-indigo-600 hover:underline inline-flex items-center gap-0.5" onClick={() => openSbPreview(r.sb_id)} data-testid={`mgmt-tally-sb-${r.invoice_no}`} title="Open shipping bill preview">
                                <FileText className="w-3.5 h-3.5" />{r.sb_no ? <span className="font-mono">{r.sb_no}</span> : null}
                              </button>
                            ) : <span className="text-zinc-300">—</span>}
                          </td>
                          {canReconcile && (
                            <td className="px-2 py-1.5 text-center whitespace-nowrap">
                              {r.status === "match" ? <span className="text-zinc-300">—</span>
                                : r.resolved ? (
                                  <span className="inline-flex items-center gap-1">
                                    <span className="inline-flex items-center gap-0.5 text-emerald-700" title={`${r.resolution?.note || ""}${r.resolution?.by ? " — " + r.resolution.by : ""}`} data-testid={`mgmt-tally-resolved-${r.invoice_no}`}><CheckCircle2 className="w-3.5 h-3.5" /> Resolved</span>
                                    <button className="text-zinc-400 hover:text-red-600" onClick={() => reopenTallyIssue(r.invoice_no)} data-testid={`mgmt-tally-reopen-${r.invoice_no}`} title="Re-open this issue"><RotateCcw className="w-3.5 h-3.5" /></button>
                                  </span>
                                ) : (
                                  <button className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded border border-amber-300 text-amber-800 hover:bg-amber-50" onClick={() => setResolveDlg({ open: true, row: r, note: "", busy: false })} data-testid={`mgmt-tally-resolve-${r.invoice_no}`}>
                                    Mark reconciled
                                  </button>
                                )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-zinc-400">Compares each Tally invoice's taxable value against the ERP's shipping-bill FOB value (₹), matched by invoice number, exact to the rupee.</p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Resolve a Tally discrepancy with a note */}
      <Dialog open={resolveDlg.open} onOpenChange={(o) => !o && !resolveDlg.busy && setResolveDlg({ open: false, row: null, note: "", busy: false })}>
        <DialogContent data-testid="mgmt-tally-resolve-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Mark reconciled with a note</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-zinc-50 border rounded px-3 py-2 text-xs">
              <div className="font-mono font-semibold">{resolveDlg.row?.invoice_no}</div>
              <div className="text-zinc-500 mt-0.5">
                Tally {resolveDlg.row?.tally_taxable != null ? fmtINR(resolveDlg.row.tally_taxable) : "—"} vs ERP {resolveDlg.row?.erp_inr != null ? fmtINR(resolveDlg.row.erp_inr) : "—"}
                {resolveDlg.row?.diff != null && <> · diff {resolveDlg.row.diff > 0 ? "+" : ""}{fmtINR(resolveDlg.row.diff)}</>}
              </div>
            </div>
            <label className="block text-xs font-medium text-zinc-500">Resolution note <span className="text-red-500">*</span></label>
            <textarea
              value={resolveDlg.note}
              onChange={(e) => setResolveDlg((d) => ({ ...d, note: e.target.value }))}
              rows={3}
              className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="e.g. FX rounding difference, verified against invoice — OK / Credit note issued / Correction pending in Tally"
              data-testid="mgmt-tally-resolve-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDlg({ open: false, row: null, note: "", busy: false })} disabled={resolveDlg.busy}>Cancel</Button>
            <Button onClick={resolveTallyIssue} disabled={resolveDlg.busy || !resolveDlg.note.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mgmt-tally-resolve-submit">
              {resolveDlg.busy ? "Saving…" : "Mark reconciled"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </SafetyShell>
  );
}

export default ManagementReport;
